import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { VIRAL_ECHO_PLUS_IN_CIRCLE } from '../config/viralEchoHealEffect';
import { getGeneralZoneRect } from '../utils/generalPortraitAnchor';
import './ViralEchoHealPlusEffect.css';

/** Pixel centers for each × inside the circular profile (inscribed in rect). */
function damageMarkPositionsInProfile(rect) {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const halfMin = Math.min(rect.width, rect.height) / 2;

  return VIRAL_ECHO_PLUS_IN_CIRCLE.map(({ angleDeg, radiusFrac }) => {
    const dist = halfMin * radiusFrac * 2;
    const rad = (angleDeg * Math.PI) / 180;
    return {
      x: cx + dist * Math.cos(rad),
      y: cy + dist * Math.sin(rad),
    };
  });
}

/**
 * Three red × marks in quick succession inside the enemy general portrait (185 / 188 / 220).
 * Heal side uses GeneralHealFlashEffect (heal.png).
 */
export function ViralEchoHealPlusEffect({ generalDataZone, animationKey, variant = 'damage' }) {
  const [points, setPoints] = useState(null);

  useLayoutEffect(() => {
    if (variant !== 'damage') {
      setPoints(null);
      return undefined;
    }

    let cancelled = false;
    const measure = (attempt) => {
      if (!generalDataZone) {
        if (!cancelled) setPoints(null);
        return;
      }
      const rect = getGeneralZoneRect(generalDataZone);
      if (rect) {
        if (!cancelled) setPoints(damageMarkPositionsInProfile(rect));
        return;
      }
      if (attempt < 24 && !cancelled) {
        requestAnimationFrame(() => {
          if (!cancelled) measure(attempt + 1);
        });
      }
    };
    setPoints(null);
    measure(0);
    return () => {
      cancelled = true;
    };
  }, [generalDataZone, animationKey, variant]);

  if (variant !== 'damage' || !points || points.length === 0) {
    return null;
  }

  const layer = (
    <div
      className="viral-echo-heal-layer"
      aria-hidden
      data-animation-key={animationKey}
    >
      {points.map((pt, i) => (
        <div
          key={i}
          className="viral-echo-heal-mark"
          style={{
            left: `${pt.x}px`,
            top: `${pt.y}px`,
          }}
        >
          <span className={`viral-echo-damage-x viral-echo-damage-x--${i}`}>×</span>
        </div>
      ))}
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(layer, document.body);
  }
  return layer;
}

export default ViralEchoHealPlusEffect;
